import { useMemo, useState } from "react";
import { CheckIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";

import highlightJson from "../../../utils/highlightJson";

/** Pretty-printed, highlighted json with a copy button. Falls back to the raw string if it won't parse. */
export default function RawJsonViewer({ json, maxHeight = "max-h-[60vh]" }: { json?: string | object | null; maxHeight?: string }) {
  const [copied, setCopied] = useState(false);

  const { text, isValid } = useMemo(() => {
    if (json == null || json === "") return { text: "", isValid: true };
    if (typeof json !== "string") return { text: JSON.stringify(json, null, 2), isValid: true };
    try {
      return { text: JSON.stringify(JSON.parse(json), null, 2), isValid: true };
    } catch {
      return { text: json, isValid: false };
    }
  }, [json]);

  if (!text) return <p className="text-sm text-gray-500">No data recorded.</p>;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be blocked (e.g. inside the Core dashboard iframe); the text is still selectable.
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={copy}
        className="absolute right-2 top-2 inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
      >
        {copied ? <CheckIcon className="h-4 w-4 text-green-600" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
        {copied ? "Copied" : "Copy"}
      </button>
      <pre className={`overflow-auto whitespace-pre-wrap break-words rounded border border-gray-200 bg-gray-50 p-3 pr-20 text-xs text-gray-800 ${maxHeight}`}>
        {isValid ? highlightJson(text) : text}
      </pre>
      {!isValid && <p className="mt-2 text-xs text-amber-700">Invalid JSON string. Showing raw value.</p>}
    </div>
  );
}
